import React, { ComponentProps, useMemo } from 'react';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';
import { useRouter } from 'next/router';
import Icon from '@/components/ui/Icon';
import { Size } from '@/types/enums';
import { MergedSingleDrop } from '@/types/singleDrop';
import DropCard from '@/components/DropCard';

import styles from './DropList.module.css';
import { ROUTE } from '@/lib/constants';

type DropListProps = Pick<ComponentProps<typeof DropCard>, 'isLoading'> & {
  singleDrops: MergedSingleDrop[];
};

export default function DropList(
  { singleDrops, isLoading }: DropListProps,
): React.ReactElement {
  const router = useRouter();

  const getTitle = useMemo(() => (
    router.pathname.startsWith(ROUTE.INVENTORY_ROUTE)
      ? <FormattedMessage id="drop_list.name-alternative" />
      : <FormattedMessage id="drop_list.name" />
  ), []);

  const getContent = () => singleDrops.map((singleDrop) => (
    <DropCard singleDrop={singleDrop} isLoading={isLoading} key={`drop-list-item-${singleDrop.id}`} />
  ));

  return (
    <div className={styles['drop-list']}>
      <div className={styles['drop-list__title-container']}>
        <Icon size={Size.LARGE} iconName="drop_icon" />
        <h3 className={styles['drop-list__title']}>
          {getTitle}
        </h3>
      </div>
      {singleDrops.length ? (
        <div className={styles['drop-list__content-container']}>
          {getContent()}
        </div>
      ) : (
        <p className="inventory-empty-placeholder">
          <FormattedMessage
            id="drop_list.empty_message"
            values={{
              a: (text) => (
                <Link
                  className="inventory-empty-link"
                  href={ROUTE.INDEX}
                >
                  {text}
                </Link>
              ),
            }}
          />
        </p>
      )}
    </div>
  );
}
